import React, {useCallback, useEffect, useRef, useState} from "react";
import classnames from "classnames";

import styles from "./ActionMenu.module.css";

export interface ActionMenuItem {
    label: string
    onClick: () => void
    iconSrc?: string
    disabled?: boolean
    /** Renders in the destructive style and is separated from the actions above it. */
    danger?: boolean
}

interface ActionMenuProps {
    items: ActionMenuItem[]
    title?: string
}

/**
 * Overflow menu for secondary page actions.
 *
 * The assistant page header had grown to five inline buttons before export, import and clone were
 * added; putting the less frequent actions behind one trigger keeps the primary ones readable.
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({items, title = "More actions"}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const close = useCallback((returnFocus: boolean = false) => {
        setIsOpen(false);
        setFocusedIndex(-1);
        if (returnFocus) {
            triggerRef.current?.focus();
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                close();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [isOpen, close]);

    useEffect(() => {
        if (isOpen && focusedIndex >= 0) {
            itemRefs.current[focusedIndex]?.focus();
        }
    }, [isOpen, focusedIndex]);

    const selectableIndexes = items
        .map((item, index) => (item.disabled ? -1 : index))
        .filter(index => index >= 0);

    const moveFocus = (direction: 1 | -1) => {
        if (selectableIndexes.length === 0) {
            return;
        }

        const currentPosition = selectableIndexes.indexOf(focusedIndex);
        const nextPosition = currentPosition === -1
            ? (direction === 1 ? 0 : selectableIndexes.length - 1)
            : (currentPosition + direction + selectableIndexes.length) % selectableIndexes.length;

        setFocusedIndex(selectableIndexes[nextPosition]);
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
            moveFocus(event.key === "ArrowDown" ? 1 : -1);
        }
    };

    const handleMenuKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape") {
            event.preventDefault();
            close(true);
        } else if (event.key === "ArrowDown") {
            event.preventDefault();
            moveFocus(1);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            moveFocus(-1);
        }
    };

    const handleItemClick = (item: ActionMenuItem) => {
        if (item.disabled) {
            return;
        }
        close();
        item.onClick();
    };

    return <div className={styles.container} ref={containerRef}>
        <button
            type="button"
            ref={triggerRef}
            className={styles.trigger}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={title}
            onClick={() => (isOpen ? close() : setIsOpen(true))}
            onKeyDown={handleTriggerKeyDown}
        >
            <span className={styles.triggerLabel}>{title}</span>
            <span className={styles.triggerGlyph} aria-hidden="true">⋯</span>
        </button>

        {isOpen && <div className={styles.menu} role="menu" onKeyDown={handleMenuKeyDown}>
            {items.map((item, index) => (
                <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    ref={element => {
                        itemRefs.current[index] = element;
                    }}
                    className={classnames(styles.item, {
                        [styles.itemDisabled]: item.disabled,
                        [styles.itemDanger]: item.danger
                    })}
                    disabled={item.disabled}
                    onClick={() => handleItemClick(item)}
                >
                    {item.iconSrc && <img className={styles.itemIcon} src={item.iconSrc} alt=""/>}
                    <span>{item.label}</span>
                </button>
            ))}
        </div>}
    </div>
}
